import { t } from "@lingui/core/macro";
import { confirmSignIn, signIn } from "aws-amplify/auth";
import { type FormEvent, useState } from "react";

import { type ChallengedUser } from "@/routes/_unauthenticated/sign-in";
import { AsyncButton } from "@/shared/components/ui/async-button";
import { Button } from "@/shared/components/ui/button";

import AuthPage from "./AuthPage";
import PasswordInput from "./PasswordInput";

export default function RecoveryCode({
  email,
  setChallengedUser,
}: {
  email: string;
  setChallengedUser: (challengedUser: ChallengedUser | null) => void;
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>("");

  const handleRecoveryCode = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const recoveryCode = formData.get("recovery-code") as string;

    setError("");
    setLoading(true);

    try {
      await signIn({ username: email, options: { authFlowType: "CUSTOM_WITHOUT_SRP" } });
      const result = await confirmSignIn({ challengeResponse: recoveryCode });
      if (result.isSignedIn) setChallengedUser(null);
    } catch (err) {
      const errorMessage = (err as Error).message;

      if (errorMessage.includes("expired") || errorMessage.includes("attempts")) {
        setError(errorMessage.split("error")![1].trim());
      } else {
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthPage title={t`Enter Recovery Code`}>
      <form className="flex flex-col gap-4" onSubmit={handleRecoveryCode}>
        <PasswordInput placeholder={t`Enter your Recovery Code`} name="recovery-code" required />
        {error && <p className="text-sm text-red-500">{error}</p>}
        <div className="flex flex-col gap-2">
          <AsyncButton type="submit" loading={loading} loadingText={t`Verifying`} minLoadTime={1500}>
            {t`Submit`}
          </AsyncButton>
        </div>
        <Button
          variant="link"
          type="button"
          onClick={() => {
            setChallengedUser(null);
          }}
        >
          {t`Back to Sign In`}
        </Button>
      </form>
    </AuthPage>
  );
}
